import { useState, useEffect } from "react";
import { useParams } from "react-router-dom"; // Get topicId from URL
import NodeAPI from "../api"; // API helper
import Fuse from "fuse.js"; // Fuzzy Matching

const QuizPlayer = () => {
    const { topicId } = useParams(); // Get the topic ID from the URL
    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [userAnswer, setUserAnswer] = useState("");
    const [isListening, setIsListening] = useState(false);
    const [feedback, setFeedback] = useState("");
    const [language, setLanguage] = useState("en-US");
    const [timeLeft, setTimeLeft] = useState(10);
    const [customTime, setCustomTime] = useState(10);
    const [timerActive, setTimerActive] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch questions from backend
    useEffect(() => {
        if (!topicId) return;

        NodeAPI.get(`/quiz/quiz/gamified/${topicId}`)
            .then(({ data }) => {
                setQuestions(data);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching questions:", err);
                setError("Failed to load questions.");
                setLoading(false);
            });
    }, [topicId]);

    // Read question aloud
    useEffect(() => {
        if (questions.length > 0 && questions[currentQuestionIndex]) {
            readQuestion(questions[currentQuestionIndex]); // Ensure valid question
        }
    }, [currentQuestionIndex, questions]);
    
    

    const readQuestion = (question) => {
        if (!question || !question.question) return; // Prevent undefined access
    
        const utterance = new SpeechSynthesisUtterance();
        utterance.lang = language;
        utterance.text = question.question;
    
        if (question.options) {
            utterance.text += " Options are: " + question.options.join(", ");
        }
    
        utterance.onend = () => {
            startTimer();
        };
    
        window.speechSynthesis.speak(utterance);
    };
    
    

    const startListening = () => {
        if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
            alert("Speech recognition is not supported in this browser.");
            return;
        }

        const recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();
        recognition.lang = language;
        recognition.continuous = false;
        recognition.interimResults = false;

        recognition.onstart = () => setIsListening(true);
        recognition.onend = () => setIsListening(false);

        recognition.onresult = (event) => {
            const transcript = event.results[0][0].transcript.toLowerCase().trim();
            setUserAnswer(transcript);
            checkAnswer(transcript);
        };

        recognition.start();
    };

    const checkAnswer = (spokenAnswer) => {
        if (!questions.length || !questions[currentQuestionIndex]) {
            console.error("No question found at index:", currentQuestionIndex);
            return;
        }
    
        const correctAnswer = questions[currentQuestionIndex].correct_answer; // Use correct_answer instead of answer
        
        if (!correctAnswer) {
            console.error("Answer is missing for question:", questions[currentQuestionIndex]);
            return;
        }
    
        const lowerCorrectAnswer = correctAnswer.toLowerCase().trim();
        const fuse = new Fuse([lowerCorrectAnswer], { includeScore: true, threshold: 0.4 });
        const result = fuse.search(spokenAnswer.toLowerCase().trim());
    
        if (result.length > 0 && result[0].score < 0.4) {
            setFeedback("✅ Correct!");
            setTimeout(nextQuestion, 2000);
        } else {
            setFeedback("❌ Try again!");
        }
    };
    
    

    const startTimer = () => {
        setTimeLeft(customTime);
        setTimerActive(true);

        const timer = setInterval(() => {
            setTimeLeft((prevTime) => {
                if (prevTime === 1) {
                    clearInterval(timer);
                    setTimerActive(false);
                    setFeedback("⏳ Time's up! Moving to next question...");
                    setTimeout(nextQuestion, 2000);
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);
    };

    const nextQuestion = () => {
        setFeedback(""); // Reset feedback
        setUserAnswer(""); // Reset answer
    
        setCurrentQuestionIndex((prevIndex) => {
            if (prevIndex < questions.length - 1) {
                return prevIndex + 1;
            } else {
                setFeedback("🎉 Quiz Completed!");
                return prevIndex; // Stay on the last question
            }
        });
    };
    
    

  
    if (loading) return <p>Loading quiz...</p>;
    if (error) return <p style={{ color: "red" }}>{error}</p>;
    if (!questions.length || !questions[currentQuestionIndex]) return <p>No questions available.</p>;
    
    return (
        <div style={styles.container}>
            <h2>Voice Quiz Mode</h2>
    
            {/* Language Selection */}
            <label>
                Select Language:
                <select value={language} onChange={(e) => setLanguage(e.target.value)} style={styles.select}>
                    <option value="en-US">English</option>
                    <option value="es-ES">Spanish</option>
                    <option value="fr-FR">French</option>
                    <option value="de-DE">German</option>
                    <option value="hi-IN">Hindi</option>
                    <option value="zh-CN">Chinese</option>
                </select>
            </label>
    
            {/* Custom Timer Input */}
            <label>
                Set Timer (seconds):
                <input 
                    type="number" 
                    value={customTime} 
                    onChange={(e) => setCustomTime(Number(e.target.value))} 
                    style={styles.input}
                    min="5"
                />
            </label>
    
            <h3 style={styles.questionBox}>
    {questions[currentQuestionIndex]?.question || "Loading question..."}
</h3>

{questions[currentQuestionIndex]?.options && (
    <ul style={styles.optionsList}>
        {questions[currentQuestionIndex].options.map((option, index) => (
            <li 
                key={index} 
                style={styles.optionItem} 
                onMouseOver={(e) => e.target.style.backgroundColor = styles.optionItemHover.backgroundColor}
                onMouseOut={(e) => e.target.style.backgroundColor = styles.optionItem.backgroundColor}
            >
                {option}
            </li>
        ))}
    </ul>
)}
    
    <p style={styles.timer}>⏳ Time left: {timeLeft}s</p>
    
            <button 
    onClick={startListening} 
    style={styles.button} 
    onMouseOver={(e) => e.target.style.backgroundColor = styles.buttonHover.backgroundColor}
    onMouseOut={(e) => e.target.style.backgroundColor = styles.button.backgroundColor}
>
    {isListening ? "🎙️ Listening..." : "🎤 Speak Answer"}
</button>

{/* Repeat Question Button */}
<button 
    onClick={() => readQuestion(questions[currentQuestionIndex])} 
    style={styles.button} 
    onMouseOver={(e) => e.target.style.backgroundColor = styles.buttonHover.backgroundColor}
    onMouseOut={(e) => e.target.style.backgroundColor = styles.button.backgroundColor}
>
    🔁 Repeat Question
</button>
    
<p><strong>Your Answer:</strong> {userAnswer}</p>
<p><strong>Feedback:</strong> {feedback}</p>
        </div>
    );
    
};

const styles = {
   button: { padding: "10px", margin: "5px", backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "5px" },
    select: { marginLeft: "10px", padding: "5px" },
    input: { marginLeft: "10px", padding: "5px", width: "60px" },
    container: {
        textAlign: "center",
        padding: "20px",
        maxWidth: "600px",
        margin: "auto",
        backgroundColor: "#f9f9f9",
        borderRadius: "10px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    },
    questionBox: {
        backgroundColor: "#fff",
        padding: "15px",
        borderRadius: "8px",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        marginBottom: "15px",
        fontSize: "18px",
        fontWeight: "bold",
    },
    optionsList: {
        listStyle: "none",
        padding: "0",
    },
    optionItem: {
        backgroundColor: "#007bff",
        color: "white",
        padding: "10px",
        margin: "5px 0",
        borderRadius: "5px",
        cursor: "pointer",
        transition: "background 0.3s",
    },
    optionItemHover: {
        backgroundColor: "#0056b3",
    },
    timer: {
        fontSize: "16px",
        fontWeight: "bold",
        margin: "10px 0",
        color: "#d9534f",
    },
    button: {
        padding: "10px",
        margin: "5px",
        backgroundColor: "#28a745",
        color: "white",
        border: "none",
        borderRadius: "5px",
        cursor: "pointer",
        transition: "background 0.3s",
    },
    buttonHover: {
        backgroundColor: "#218838",
    },
    select: {
        marginLeft: "10px",
        padding: "5px",
    },
    input: {
        marginLeft: "10px",
        padding: "5px",
        width: "60px",
    },
};

    


export default QuizPlayer; 